// useSettingsPlanPaneState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// Services
import { getWorkspaceApiLimits } from 'services/api/ApiAccessService';
import { getWorkspaceBillingRecord } from 'services/billing/WorkspaceBillingService';
import { listActivePlans } from 'services/plans/PlanService';
import {
    cancelWorkspaceStripeSubscription,
    createWorkspaceBillingPortalSession,
} from 'services/billing/WorkspaceSubscriptionWriteService';
import { getWorkspaceLiveUsageSnapshot } from 'services/workspaces/WorkspaceAdminService';
import { updateWorkspacePlanSelection } from 'services/workspaces/WorkspaceWriteService';

// Utils
import {
    getDefaultFreePlan,
    isSelfServeSelectable,
} from '../utils/settingsPlanPaneUtils';

/**
 * useSettingsPlanPaneState
 *
 * Manages plan catalog loading, billing state, usage snapshots, and billing actions for SettingsPlanPane.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.user - Active user model.
 * @param {object} params.workspace - Active workspace model.
 * @param {boolean} params.canEditWorkspace - Whether the current user can edit workspace settings.
 *
 * @returns {object} Grouped pane state and handlers.
 */
const useSettingsPlanPaneState = ({
    user,
    workspace,
    canEditWorkspace,
}) => {
    const { setActiveWorkspace } = useWorkspace();

    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [savingPlanKey, setSavingPlanKey] = useState('');
    const [billingRecord, setBillingRecord] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);
    const [apiLimits, setApiLimits] = useState(null);
    const [apiUsageLoading, setApiUsageLoading] = useState(false);
    const [apiUsageError, setApiUsageError] = useState('');
    const [checkoutPlanKey, setCheckoutPlanKey] = useState('');
    const [isCancelSubscriptionConfirming, setIsCancelSubscriptionConfirming] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
    const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false);
    const [liveUsage, setLiveUsage] = useState(null);
    const [liveUsageLoading, setLiveUsageLoading] = useState(false);
    const [liveUsageError, setLiveUsageError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        let isActive = true;

        const loadPlans = async () => {
            setIsLoading(true);
            setErrorMessage('');

            try {
                const nextPlans = await listActivePlans();
                if (!isActive) return;
                setPlans(nextPlans);
            } catch (error) {
                if (!isActive) return;
                setPlans([]);
                setErrorMessage(error?.message || 'Failed to load workspace plans.');
            } finally {
                if (!isActive) return;
                setIsLoading(false);
            }
        };

        loadPlans();

        return () => {
            isActive = false;
        };
    }, [workspace?.key]);

    useEffect(() => {
        const workspaceKey = String(workspace?.key || '').trim();
        if (!workspaceKey || !canEditWorkspace) {
            setBillingRecord(null);
            setBillingLoading(false);
            return;
        }

        let isActive = true;

        const loadBillingRecord = async () => {
            setBillingLoading(true);

            try {
                const nextBillingRecord = await getWorkspaceBillingRecord(workspaceKey);
                if (!isActive) return;
                setBillingRecord(nextBillingRecord);
            } catch {
                if (!isActive) return;
                setBillingRecord(null);
            } finally {
                if (!isActive) return;
                setBillingLoading(false);
            }
        };

        loadBillingRecord();

        return () => {
            isActive = false;
        };
    }, [canEditWorkspace, workspace?.key]);

    useEffect(() => {
        const workspaceKey = String(workspace?.key || '').trim();
        if (!workspaceKey) {
            setApiLimits(null);
            setApiUsageError('');
            setApiUsageLoading(false);
            return;
        }

        let isActive = true;

        const loadApiLimits = async () => {
            setApiUsageLoading(true);
            setApiUsageError('');

            try {
                const nextApiLimits = await getWorkspaceApiLimits({ workspaceId: workspaceKey });
                if (!isActive) return;
                setApiLimits(nextApiLimits || null);
            } catch (error) {
                if (!isActive) return;
                setApiLimits(null);
                setApiUsageError(error?.message || 'Could not load API usage.');
            } finally {
                if (!isActive) return;
                setApiUsageLoading(false);
            }
        };

        loadApiLimits();

        return () => {
            isActive = false;
        };
    }, [workspace?.key]);

    useEffect(() => {
        const workspaceKey = String(workspace?.key || '').trim();
        if (!workspaceKey) {
            setLiveUsage(null);
            setLiveUsageError('');
            setLiveUsageLoading(false);
            return;
        }

        let isActive = true;

        const loadLiveUsage = async () => {
            setLiveUsageLoading(true);
            setLiveUsageError('');

            try {
                const nextLiveUsage = await getWorkspaceLiveUsageSnapshot({ workspaceKey });
                if (!isActive) return;
                setLiveUsage(nextLiveUsage || null);
            } catch (error) {
                if (!isActive) return;
                setLiveUsage(null);
                setLiveUsageError(error?.message || 'Could not load live usage.');
            } finally {
                if (!isActive) return;
                setLiveUsageLoading(false);
            }
        };

        loadLiveUsage();

        return () => {
            isActive = false;
        };
    }, [workspace?.key]);

    const currentPlanKey = String(workspace?.planKey || '').trim();

    const currentPlan = useMemo(() => {
        if (!currentPlanKey) return null;
        return plans.find((plan) => String(plan?.key || '').trim() === currentPlanKey) || null;
    }, [currentPlanKey, plans]);

    const currentPlanTitle = String(currentPlan?.title || workspace?.planTitle || '').trim() || 'No plan assigned';
    const isCurrentPlanHiddenFromCatalog = Boolean(currentPlanKey) && !currentPlan;

    const hasActivePaidSubscription = useMemo(() => {
        const billingStatus = String(billingRecord?.billingStatus || '').trim();
        const subscriptionId = String(billingRecord?.stripeSubscriptionId || '').trim();
        if (!subscriptionId) return false;

        return Boolean(billingStatus && billingStatus !== 'canceled' && billingStatus !== 'inactive');
    }, [billingRecord?.billingStatus, billingRecord?.stripeSubscriptionId]);

    const checkoutPlan = useMemo(() => {
        if (!checkoutPlanKey) return null;
        return plans.find((plan) => String(plan?.key || '').trim() === checkoutPlanKey) || null;
    }, [checkoutPlanKey, plans]);

    const billingSubscriptionId = String(billingRecord?.stripeSubscriptionId || '').trim();

    const defaultFreePlan = useMemo(() => {
        return getDefaultFreePlan(plans);
    }, [plans]);

    const canCancelPaidSubscription = Boolean(
        canEditWorkspace
        && billingSubscriptionId
        && hasActivePaidSubscription
        && !isCancellingSubscription
        && !savingPlanKey
    );

    const canOpenBillingPortal = Boolean(
        canEditWorkspace
        && String(billingRecord?.stripeCustomerId || '').trim()
        && !isCancellingSubscription
        && !isOpeningBillingPortal
        && !savingPlanKey
    );

    /**
     * applyWorkspacePlanAssignment
     *
     * Applies a workspace plan assignment to the local workspace context.
     *
     * @param {object} params - Assignment values.
     * @param {string|null} params.planKey - Next workspace plan key.
     * @param {string|null} params.planTitle - Next workspace plan title.
     * @returns {void}
     */
    const applyWorkspacePlanAssignment = useCallback(({ planKey, planTitle }) => {
        setActiveWorkspace((previous) => {
            if (!previous || previous.key !== workspace?.key) return previous;

            return {
                ...previous,
                dateModified: new Date(),
                planAssignedAt: new Date(),
                planAssignedBy: user?.key || null,
                planKey: planKey || null,
                planTitle: planTitle || null,
            };
        });
    }, [setActiveWorkspace, user?.key, workspace?.key]);

    /**
     * handleSelectPlan
     *
     * Assigns a self-serve plan to the current workspace.
     *
     * @param {object} plan - Selected public plan row.
     * @returns {Promise<void>} Resolves when the plan assignment completes.
     */
    const handleSelectPlan = useCallback(async (plan) => {
        const nextPlanKey = String(plan?.key || '').trim();
        const nextPlanTitle = String(plan?.title || '').trim();

        if (savingPlanKey || !workspace?.key || !nextPlanKey) return;

        setCheckoutPlanKey('');
        setIsCancelSubscriptionConfirming(false);

        if (!isSelfServeSelectable(plan)) {
            setErrorMessage('Paid self-serve plan changes are not enabled yet.');
            setStatusMessage('');
            return;
        }

        if (hasActivePaidSubscription) {
            setErrorMessage('Cancel the active paid subscription before moving this workspace to a free plan.');
            setStatusMessage('');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Updating workspace plan...');
        setSavingPlanKey(nextPlanKey);

        try {
            await updateWorkspacePlanSelection({
                workspaceKey: workspace.key,
                planKey: nextPlanKey,
                planTitle: nextPlanTitle,
                assignedBy: user?.key || '',
            });

            applyWorkspacePlanAssignment({
                planKey: nextPlanKey,
                planTitle: nextPlanTitle,
            });

            setStatusMessage(`${nextPlanTitle} is now active for this workspace.`);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update workspace plan.');
            setStatusMessage('');
        } finally {
            setSavingPlanKey('');
        }
    }, [applyWorkspacePlanAssignment, hasActivePaidSubscription, savingPlanKey, user?.key, workspace?.key]);

    /**
     * handleStartPaidCheckout
     *
     * Opens inline checkout for a paid plan.
     *
     * @param {object} plan - Selected paid plan row.
     * @returns {void}
     */
    const handleStartPaidCheckout = useCallback((plan) => {
        const nextPlanKey = String(plan?.key || '').trim();
        if (!nextPlanKey) return;

        setErrorMessage('');
        setStatusMessage('');
        setCheckoutPlanKey(nextPlanKey);
        setIsCancelSubscriptionConfirming(false);
    }, []);

    /**
     * handleCancelPaidCheckout
     *
     * Closes inline paid checkout.
     *
     * @returns {void}
     */
    const handleCancelPaidCheckout = useCallback(() => {
        setCheckoutPlanKey('');
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    /**
     * handleStartCancelSubscription
     *
     * Opens the subscription cancellation confirmation state.
     *
     * @returns {void}
     */
    const handleStartCancelSubscription = useCallback(() => {
        setCheckoutPlanKey('');
        setErrorMessage('');
        setStatusMessage('');
        setIsCancelSubscriptionConfirming(true);
    }, []);

    /**
     * handleAbortCancelSubscription
     *
     * Closes the subscription cancellation confirmation state.
     *
     * @returns {void}
     */
    const handleAbortCancelSubscription = useCallback(() => {
        setIsCancelSubscriptionConfirming(false);
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    /**
     * handleCancelSubscription
     *
     * Cancels the active paid subscription for this workspace.
     *
     * @returns {Promise<void>} Resolves when the cancel flow completes.
     */
    const handleCancelSubscription = useCallback(async () => {
        if (!workspace?.key || !billingSubscriptionId || isCancellingSubscription) return;

        setCheckoutPlanKey('');
        setErrorMessage('');
        setStatusMessage('Cancelling workspace subscription...');
        setIsCancelSubscriptionConfirming(false);
        setIsCancellingSubscription(true);

        try {
            const result = await cancelWorkspaceStripeSubscription({
                subscriptionId: billingSubscriptionId,
                workspaceKey: workspace.key,
            });

            const nextBillingRecord = await getWorkspaceBillingRecord(workspace.key);
            setBillingRecord(nextBillingRecord);

            const nextPlanKey = String(
                result?.effectiveWorkspacePlanKey
                || defaultFreePlan?.key
                || ''
            ).trim();
            const nextPlanTitle = String(
                result?.effectiveWorkspacePlanTitle
                || defaultFreePlan?.title
                || ''
            ).trim();

            applyWorkspacePlanAssignment({
                planKey: nextPlanKey,
                planTitle: nextPlanTitle,
            });

            if (nextPlanTitle) {
                setStatusMessage(`Subscription canceled. ${nextPlanTitle} is now active for this workspace.`);
            } else {
                setStatusMessage('Subscription canceled. This workspace no longer has an assigned plan.');
            }
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to cancel the active workspace subscription.');
            setStatusMessage('');
        } finally {
            setIsCancellingSubscription(false);
        }
    }, [applyWorkspacePlanAssignment, billingSubscriptionId, defaultFreePlan?.key, defaultFreePlan?.title, isCancellingSubscription, workspace?.key]);

    /**
     * handleOpenBillingPortal
     *
     * Opens the Stripe billing portal for this workspace in a new tab.
     *
     * @returns {Promise<void>} Resolves when the portal flow completes.
     */
    const handleOpenBillingPortal = useCallback(async () => {
        if (!workspace?.key || !canOpenBillingPortal) return;

        setErrorMessage('');
        setStatusMessage('Opening billing portal...');
        setIsOpeningBillingPortal(true);

        try {
            const result = await createWorkspaceBillingPortalSession({
                returnUrl: window.location.href,
                workspaceKey: workspace.key,
            });

            const portalUrl = String(result?.url || '').trim();
            if (!portalUrl) {
                throw new Error('Stripe billing portal did not return a URL.');
            }

            window.open(portalUrl, '_blank', 'noopener,noreferrer');
            setStatusMessage('Billing portal opened in a new tab.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to open the billing portal.');
            setStatusMessage('');
        } finally {
            setIsOpeningBillingPortal(false);
        }
    }, [canOpenBillingPortal, workspace?.key]);

    /**
     * handlePaidCheckoutSuccess
     *
     * Handles successful paid subscription creation.
     *
     * @param {object} input - Success payload.
     * @param {string} input.billingEmail - Billing email returned by checkout.
     * @param {string} input.billingName - Billing name returned by checkout.
     * @param {object} input.subscriptionResult - Subscription creation result payload.
     * @returns {Promise<void>} Resolves when local billing state is refreshed.
     */
    const handlePaidCheckoutSuccess = useCallback(async ({ billingEmail, billingName, subscriptionResult }) => {
        if (!checkoutPlan || !workspace?.key) {
            setCheckoutPlanKey('');
            return;
        }

        try {
            const effectivePlanKey = String(
                subscriptionResult?.effectiveWorkspacePlanKey
                || checkoutPlan.key
                || ''
            ).trim();
            const effectivePlanTitle = String(
                subscriptionResult?.effectiveWorkspacePlanTitle
                || checkoutPlan.title
                || ''
            ).trim();

            await updateWorkspacePlanSelection({
                workspaceKey: workspace.key,
                planKey: effectivePlanKey,
                planTitle: effectivePlanTitle,
                assignedBy: user?.key || '',
            });

            const nextBillingRecord = await getWorkspaceBillingRecord(workspace.key);
            setBillingRecord(nextBillingRecord || {
                billingEmail,
                billingName,
                billingStatus: 'active',
                planKey: effectivePlanKey,
                planTitle: effectivePlanTitle,
                stripePriceId: checkoutPlan.stripePriceId,
            });

            applyWorkspacePlanAssignment({
                planKey: effectivePlanKey,
                planTitle: effectivePlanTitle,
            });

            setCheckoutPlanKey('');
            setStatusMessage(`${effectivePlanTitle || checkoutPlan.title} billing is now set up for this workspace.`);
            setErrorMessage('');
        } catch (error) {
            setErrorMessage(error?.message || 'Subscription was created, but the workspace plan could not be updated.');
            setStatusMessage('');
        }
    }, [applyWorkspacePlanAssignment, checkoutPlan, user?.key, workspace?.key]);

    return {
        planState: {
            plans,
            isLoading,
            savingPlanKey,
            currentPlanKey,
            currentPlan,
            currentPlanTitle,
            isCurrentPlanHiddenFromCatalog,
            checkoutPlanKey,
            checkoutPlan,
        },
        billingState: {
            billingRecord,
            billingLoading,
            billingSubscriptionId,
            hasActivePaidSubscription,
            isCancelSubscriptionConfirming,
            isCancellingSubscription,
            canCancelPaidSubscription,
            isOpeningBillingPortal,
            canOpenBillingPortal,
        },
        usageState: {
            apiLimits,
            apiUsageLoading,
            apiUsageError,
            liveUsage,
            liveUsageLoading,
            liveUsageError,
        },
        messageState: {
            errorMessage,
            statusMessage,
        },
        handlers: {
            handleSelectPlan,
            handleStartPaidCheckout,
            handleCancelPaidCheckout,
            handleStartCancelSubscription,
            handleAbortCancelSubscription,
            handleCancelSubscription,
            handleOpenBillingPortal,
            handlePaidCheckoutSuccess,
        },
    };
};

export default useSettingsPlanPaneState;